import { type FormEvent, useState } from "react"
import { api } from "../lib/api"
import {
  SAP_CONNECTORS, SAP_DEFAULT_CONFIG, SAP_EMPTY_POLICY, hasSapReadCheck, sapCapabilityRows, sapConnectionBody,
  type SapConnection, type SapConnector, type SapSecret,
} from "../lib/sapConnections"
import { Button, Card, ErrorNote, Field, Modal, Select, Spinner, TextArea, useLoad } from "./ui"
import type { VpnProfile } from "../lib/vpnProfiles"
import { VpnNetworkSelect } from "./vpn-profiles"

export function SapConnectionsTile({ connector, vpnProfiles = [], vpnLoading = false, vpnError }: { connector: SapConnector; vpnProfiles?: VpnProfile[]; vpnLoading?: boolean; vpnError?: unknown }) {
  const descriptor = SAP_CONNECTORS[connector]
  const path = `/${connector}/connections`
  const list = useLoad(() => api.get<{ connections: SapConnection[]; enabled: boolean }>(path), [connector])
  const [editing, setEditing] = useState<SapConnection | "new" | null>(null)
  const [report, setReport] = useState<{ label: string; capabilities: unknown } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const enabled = list.data?.enabled === true

  async function inspect(connection: SapConnection, test: boolean) {
    setBusy(connection.id)
    setError(null)
    try {
      const result = test
        ? await api.post<{ capabilities: unknown }>(`${path}/${connection.id}/test`)
        : await api.get<{ capabilities: unknown }>(`${path}/${connection.id}/capabilities`)
      setReport({ label: connection.label, capabilities: result.capabilities })
      if (test) list.reload()
    } catch (err) { setError(err) } finally { setBusy(null) }
  }

  async function remove(connection: SapConnection) {
    if (!confirm(`Remove your connection "${connection.label}"? Your agents will lose access through this connection.`)) return
    setBusy(connection.id)
    setError(null)
    try { await api.delete(`${path}/${connection.id}`); list.reload() }
    catch (err) { setError(err) } finally { setBusy(null) }
  }

  return (
    <Card className="flex flex-col items-start gap-3 p-5">
      <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-border bg-background p-3 shadow-sm">
        <img src="/logos/sap.png" alt="SAP" className="h-full w-full object-contain" />
      </div>
      <div>
        <p className="font-medium text-foreground">{descriptor.name}</p>
        <p className="text-xs text-muted">{descriptor.description}</p>
        <p className="mt-1 text-xs text-muted">Personal connection · only your agents use your SAP account.</p>
      </div>
      <ErrorNote error={list.error ?? error} />
      {list.loading && !list.data ? <Spinner label="Loading your connections" /> : null}
      {list.data && !enabled && <p className="text-xs text-warning">This connector has been disabled for your company.</p>}
      <div className="flex w-full flex-col gap-3">
        {list.data?.connections.map((connection) => (
          <div key={connection.id} className="rounded-md border border-border p-3">
            <p className="text-sm font-medium text-foreground">{connection.label}</p>
            <p className="text-xs text-muted">
              {connection.config.system} · client {connection.config.client} · {connection.config.environment}
              {connection.active ? "" : " · inactive"}
            </p>
            <p className="mt-1 text-xs text-muted">{hasSapReadCheck(connection.capabilities) ? "Read check recorded · see capability results" : "Saved · read access not tested"}</p>
            <p className="mt-1 text-xs text-muted">Network: {connection.vpnProfileId ? vpnProfiles.find((profile) => profile.id === connection.vpnProfileId)?.label ?? "Company VPN" : "Direct"}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" disabled={!enabled || busy !== null} onClick={() => setEditing(connection)}>Edit</Button>
              <Button size="sm" variant="ghost" disabled={!enabled || !connection.active || busy !== null} onClick={() => void inspect(connection, true)}>Test read access</Button>
              <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void inspect(connection, false)}>Capabilities</Button>
              <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => void remove(connection)}>Remove</Button>
            </div>
          </div>
        ))}
      </div>
      {busy && <Spinner label="Checking your connection" />}
      <div className="mt-auto pt-1">
        <Button disabled={!enabled || busy !== null} onClick={() => setEditing("new")}>Add my connection</Button>
      </div>
      {editing && (
        <Modal wide title={editing === "new" ? `Add my ${descriptor.name} connection` : `Edit ${editing.label}`} onClose={() => setEditing(null)}>
          <SapConnectionForm connector={connector} connection={editing === "new" ? undefined : editing} vpnProfiles={vpnProfiles} vpnLoading={vpnLoading} vpnError={vpnError}
            onCancel={() => setEditing(null)} onDone={() => { setEditing(null); list.reload() }} />
        </Modal>
      )}
      {report && (
        <Modal wide title={`Capabilities · ${report.label}`} onClose={() => setReport(null)}>
          <SapCapabilities value={report.capabilities} />
        </Modal>
      )}
    </Card>
  )
}

export function SapCapabilities({ value }: { value: unknown }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">This report includes your configured settings and read check results. An enabled setting does not prove that your SAP system supports it. Live verification remains unknown until tested. Read tests do not validate writes or debugging.</p>
      <div className="max-h-[55vh] overflow-auto rounded-md border border-border">
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-border"><th className="p-3">Capability or check</th><th className="p-3">Reported result</th></tr></thead>
          <tbody>{sapCapabilityRows(value).map((row, index) => (
            <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
              <td className="p-3 align-top text-muted">{row.name}</td><td className="break-words p-3 align-top text-foreground">{row.value}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

export function SapConnectionForm({ connector, connection, onDone, onCancel, vpnProfiles = [], vpnLoading = false, vpnError }: {
  connector: SapConnector; connection?: SapConnection; onDone: () => void; onCancel: () => void; vpnProfiles?: VpnProfile[]; vpnLoading?: boolean; vpnError?: unknown
}) {
  const config = connection?.config ?? SAP_DEFAULT_CONFIG
  const [transport, setTransport] = useState(config.adtTransport)
  const [nativeRfc, setNativeRfc] = useState(Boolean(config.rfc))
  const [vpnProfileId, setVpnProfileId] = useState(connection?.vpnProfileId ?? "")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const body = sapConnectionBody(new FormData(event.currentTarget))
      const path = `/${connector}/connections`
      if (connection) await api.patch(`${path}/${connection.id}`, body)
      else await api.post(path, body)
      onDone()
    } catch (err) { setError(err) } finally { setBusy(false) }
  }

  const secret = (key: SapSecret) => ({
    placeholder: connection?.secrets[key] ? "Stored — leave blank to keep" : undefined,
    autoComplete: "off",
    hint: connection?.secrets[key] ? "Leave blank to keep your stored credential." : undefined,
  })

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <p className="text-sm text-muted">Use your own SAP account. Credentials are stored encrypted in Moderniser and used by your agents on web, desktop and CLI.</p>
      <fieldset disabled={busy} className="contents">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Connection name" name="label" defaultValue={connection?.label} placeholder="My SAP quality system" required />
          <Field label="SAP system ID" name="system" defaultValue={config.system} placeholder="SID" pattern="[A-Za-z0-9]{3}" maxLength={3} required />
          <Field label="Client" name="client" defaultValue={config.client} pattern="[0-9]{3}" maxLength={3} required />
          <Field label="Language" name="language" defaultValue={config.language} pattern="[A-Za-z]{1,2}" maxLength={2} required />
          <Select label="Environment" name="environment" defaultValue={config.environment}>
            <option value="DEV">Development (DEV)</option><option value="QAS">Quality (QAS)</option><option value="PRD">Production (PRD)</option>
          </Select>
          <Field label="Release (optional)" name="release" defaultValue={config.release} placeholder={connector === "sap-ecc" ? "ERP 6.0 EhP8" : "S/4HANA release"} />
          {connector === "sap-s4hana" ? <Select label="S/4HANA edition" name="edition" defaultValue={config.edition}>
            <option value="on-premise">On-premise</option><option value="private">Private cloud</option>
          </Select> : <input type="hidden" name="edition" value={config.edition} />}
          <Field label="Your SAP username" name="username" required={(transport !== "disabled" || nativeRfc) && !connection?.secrets.username} {...secret("username")} />
          <Field label="Your SAP password" name="password" type="password" required={(transport !== "disabled" || nativeRfc) && !connection?.secrets.password} {...secret("password")} />
        </div>
        <Toggle name="active" label="Connection active" checked={connection?.active ?? true} />
        <VpnNetworkSelect profiles={vpnProfiles} value={vpnProfileId} onChange={setVpnProfileId} loading={vpnLoading} error={vpnError} />

        <div className="flex flex-col gap-4 rounded-md border border-border p-4">
          <p className="font-medium text-foreground">ABAP and RFC access</p>
          <Select label="ABAP connection method" name="adtTransport" value={transport} onChange={(event) => setTransport(event.target.value as typeof transport)}>
            <option value="https">ADT over HTTPS</option><option value="open-rfc">ADT over RFC (experimental TypeScript adapter)</option><option value="disabled">No ABAP development connection</option>
          </Select>
          <Field label="ADT HTTPS server URL" name="adtUrl" defaultValue={config.adtUrl} placeholder="https://sap.example.com:44300" required={transport === "https"} hint="Use the server address, without /sap/bc/adt or other paths." />
          <Field label="ADT reachable private IPv4 address (optional)" name="adtAddress" defaultValue={config.adtAddress} placeholder="192.168.150.193" hint="Leave blank to use the server URL. Enter an address only if Moderniser must connect through a different private IP. The certificate is still checked against the server URL." />
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input type="checkbox" name="nativeRfc" checked={nativeRfc} onChange={(event) => setNativeRfc(event.target.checked)} className="mt-0.5" />
            Enable native RFC access
          </label>
          {(nativeRfc || transport === "open-rfc") && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="RFC application server" name="rfcHost" defaultValue={config.rfc?.host} placeholder="sap.internal.example.com" required hint="Use a private SAP hostname or IPv4 address reachable from Moderniser's server." />
              <Field label="RFC system number" name="rfcSystemNumber" defaultValue={config.rfc?.systemNumber ?? "00"} pattern="[0-9]{2}" maxLength={2} required />
              <Field label="RFC gateway port (optional)" name="rfcPort" type="number" min={1} max={65535} defaultValue={config.rfc?.port} hint="Defaults to 3300 plus the system number. This is not the SAP GUI port." />
              <Field label="RFC reachable private IPv4 address (optional)" name="rfcAddress" defaultValue={config.rfc?.address} placeholder="192.168.150.193" hint="Leave blank to use the application server. Enter an address only if Moderniser must connect through a different private IP." />
            </div>
          )}
          {transport === "open-rfc" && (
            <><p className="text-xs text-warning">RFC support varies by SAP system and data type. Test read access first; enabling development does not verify compatibility.</p>
              <Toggle name="openRfcDevelopment" label="Enable experimental ADT development over RFC" checked={config.openRfcAdtMode === "development"} /></>
          )}
          <p className="text-xs text-muted">ABAP, RFC and business API requests use the network selected above. Your company admin configures shared VPNs in Connections. For Direct access, Moderniser's server must already be able to reach your SAP system.</p>
        </div>

        <details className="rounded-md border border-border p-4" open={Boolean(config.businessUrl)}>
          <summary className="cursor-pointer text-sm font-medium text-foreground">Business HTTP APIs (optional)</summary>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Business API server URL" name="businessUrl" defaultValue={config.businessUrl} placeholder="https://sap-api.example.com" />
            <Field label="Business API reachable private IPv4 address (optional)" name="businessAddress" defaultValue={config.businessAddress} placeholder="192.168.150.193" hint="Leave blank to use the server URL. A different reachable private address keeps the original URL's certificate identity." />
            <p className="text-xs text-muted">Configure SOAP, OData or custom operations in the policy below. These credentials are separate from your ABAP account.</p>
            <Field label="Business API username" name="businessUsername" {...secret("businessUsername")} />
            <Field label="Business API password" name="businessPassword" type="password" {...secret("businessPassword")} />
            <Field label="Business API token" name="businessToken" type="password" {...secret("businessToken")} />
          </div>
        </details>

        <div className="flex flex-col gap-3 rounded-md border border-border p-4">
          <p className="font-medium text-foreground">Allowed actions</p>
          <Select label="Access policy" name="accessPolicy" defaultValue={config.accessPolicy}>
            <option value="READ_ONLY">Read only</option><option value="WRITES_WITH_APPROVAL">Writes with conversational confirmation</option><option value="BLOCKED">Blocked</option>
          </Select>
          <p className="text-xs text-muted">For a change, the agent prepares the action and asks in chat before calling it with confirmation. This does not add a separate approval screen. SAP permissions and the scopes below still apply.</p>
          <Toggle name="allowReviewedRfcCalls" label="Allow reviewed RFC calls" checked={config.allowReviewedRfcCalls} />
          <Toggle name="allowDebug" label="Allow ABAP debugging" checked={config.allowDebug} />
          <Toggle name="allowCodeExecution" label="Allow ABAP code execution" checked={config.allowCodeExecution} />
          <Toggle name="allowProductionWrites" label="Allow production writes" checked={config.allowProductionWrites} />
          <p className="text-xs text-muted">You control these permissions for this connection. SAP authorizations and the policy below still apply. Live support for writes and debugging remains unverified until tested on your system.</p>
        </div>

        <details className="rounded-md border border-border p-4">
          <summary className="cursor-pointer text-sm font-medium text-foreground">Advanced: permitted objects, RFCs and APIs</summary>
          <div className="mt-4">
            <TextArea label="Connection policy (JSON)" name="policy" rows={16}
              defaultValue={JSON.stringify(connection?.policy ?? SAP_EMPTY_POLICY, null, 2)} spellCheck={false}
              hint="Empty scopes permit no table reads, source edits, transport actions or business operations. Add reviewed RFCs, fields, editable object/package patterns and HTTP operation contracts here. Discovery alone does not enable execution." />
          </div>
        </details>
        <p className="text-xs text-muted">Creating a connection does not contact SAP. Updating settings can close an existing SAP connector session. Use Test read access to check connectivity; it does not change SAP code, execute business operations or start debugging.</p>
        <ErrorNote error={error} />
        <div className="flex gap-2"><Button type="submit">{busy ? "Saving…" : "Save my connection"}</Button><Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button></div>
      </fieldset>
    </form>
  )
}

function Toggle({ name, label, checked }: { name: string; label: string; checked: boolean }) {
  return <label className="flex items-start gap-2 text-sm text-foreground"><input type="checkbox" name={name} defaultChecked={checked} className="mt-0.5" />{label}</label>
}
